import React, { useEffect, useState } from "react";
import CrudForm from "../../components/dashboard/form/CrudForm";
import DashboardWrapper from "../../components/DashboardWrapper";
import { useSingletonCrud } from "../../hooks/useSingletonCrud";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

import {
  validateName,
  validateEmail,
  validateMobile,
} from "../../utils/Validations";


const LOCAL_STORAGE_KEY = "adminProfileData";


export default function ReadAndUpdateProfile() {

  const navigate = useNavigate();


  /* =====================================================
     CRUD
  ===================================================== */

  const {
    loading,
    errors,
    fetchSingleton,
    updateSingleton,
  } = useSingletonCrud(
    "backend/admin-profile",
    ["profile_image"]
  );


  const [
    initialData,
    setInitialData,
  ] = useState(null);


  /* =====================================================
     DB COLUMNS
  ===================================================== */

  const allowedFields = [
    "username",
    "email",
    "phone_number",
    "user_role",
    // "profile_image",
  ];


  /* =====================================================
     COMPARE DATA
  ===================================================== */

  const isEqual = (
    obj1,
    obj2
  ) => {

    if (!obj1 || !obj2) {
      return false;
    }

    return allowedFields.every(
      (key) =>
        obj1[key] === obj2[key]
    );
  };


  /* =====================================================
     LOAD PROFILE
  ===================================================== */

  useEffect(() => {

    const cached =
      localStorage.getItem(
        LOCAL_STORAGE_KEY
      );

    if (cached) {

      try {

        setInitialData(
          JSON.parse(cached)
        );

      } catch (e) {

        console.warn(
          "Failed to parse cached profile data:",
          e
        );

      }
    }


    const fetchData = async () => {

      try {

        const profile =
          await fetchSingleton();


        const formatted = {

          username:
            profile?.username ??
            "",

          email:
            profile?.email ??
            "",

          phone_number:
            profile?.phone_number ??
            "",

          profile_image:
            profile?.profile_image ??
            null,

          user_role:
            profile?.user_role ??
            "",
        };


        if (
          !isEqual(
            formatted,
            initialData
          )
        ) {

          setInitialData(
            formatted
          );

          localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify(
              formatted
            )
          );

        }

      } catch (error) {

        console.error(
          "Error fetching profile:",
          error
        );

        Swal.fire(
          "Error",
          "Failed to load profile data",
          "error"
        );

      }

    };


    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  /* =====================================================
     UPDATE PROFILE
  ===================================================== */

  const handleSubmit = async (
    formData
  ) => {

    try {

      /* =================================================
         NAME / USERNAME VALIDATION
      ================================================= */

      const usernameValidation =
        validateName(
          formData.username
        );


      if (
        usernameValidation !== true
      ) {

        Swal.fire(
          "Validation Error",
          usernameValidation,
          "warning"
        );

        return;
      }


      /* =================================================
         EMAIL VALIDATION
      ================================================= */

      const emailValidation =
        validateEmail(
          formData.email
        );


      if (
        emailValidation !== true
      ) {

        Swal.fire(
          "Validation Error",
          emailValidation,
          "warning"
        );

        return;
      }


      /* =================================================
         PHONE VALIDATION
      ================================================= */

      const phoneValidation =
        validateMobile(
          formData.phone_number
        );


      if (
        phoneValidation !== true
      ) {

        Swal.fire(
          "Validation Error",
          phoneValidation,
          "warning"
        );

        return;
      }


      /* =================================================
         DATA TO SEND
      ================================================= */

      const dataToSend = {

        username:
          formData.username
            ?.trim(),

        email:
          formData.email
            ?.trim(),

        phone_number:
          formData.phone_number
            ?.trim(),

        profile_image:
          formData.profile_image,
      };


      console.log(
        "PROFILE UPDATE DATA:",
        dataToSend
      );


      /* =================================================
         UPDATE API
      ================================================= */

      const res =
        await updateSingleton(
          dataToSend
        );


      Swal.fire(
        "Success",
        "Profile updated successfully",
        "success"
      );


      /* =================================================
         UPDATED DATA
      ================================================= */

      const updatedData = {

        username:
          res?.username ??
          dataToSend.username ??
          "",

        email:
          res?.email ??
          dataToSend.email ??
          "",

        phone_number:
          res?.phone_number ??
          dataToSend.phone_number ??
          "",

        user_role:
          res?.user_role ??
          initialData?.user_role ??
          "",

        profile_image:
          res?.profile_image ??
          initialData?.profile_image ??
          null,
      };


      setInitialData(
        updatedData
      );


      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify(
          updatedData
        )
      );


    } catch (error) {

      console.error(
        "Error updating profile:",
        error
      );


      let msg =
        "Failed to update profile";


      if (
        error.response?.data
      ) {

        if (
          error.response.data.errors
        ) {

          msg =
            Object.values(
              error.response.data.errors
            )
              .flat()
              .join(", ");

        } else if (
          error.response.data.message
        ) {

          msg =
            error.response.data.message;

        }

      }


      Swal.fire(
        "Error",
        msg,
        "error"
      );

    }

  };


  /* =====================================================
     LOADING
  ===================================================== */

  const shouldShowLoading =
    loading &&
    !initialData;


  /* =====================================================
     UI
  ===================================================== */

  return (

    <DashboardWrapper>

      <div className="container-xxl flex-grow-1 p-4">


        {/* =================================================
            BACK BUTTON
        ================================================= */}

        <div className="d-flex justify-content-between align-items-center mb-4">

          <button
            className="btn btn-secondary d-flex align-items-center"
            onClick={() =>
              navigate(-1)
            }
          >
            <span className="ms-1">
              Back
            </span>
          </button>

        </div>


        <div className="row">

          <div className="col-12">

            <div className="card">


              {/* =================================================
                  HEADER
              ================================================= */}

              <div className="card-header">

                <h1 className="h4">
                  Manage My Profile
                </h1>

              </div>


              {/* =================================================
                  BODY
              ================================================= */}

              <div className="card-body">

                {shouldShowLoading ? (

                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{
                      minHeight: "200px",
                    }}
                  >

                    <div className="spinner-border text-primary" />

                  </div>

                ) : (

                  <>

                    <CrudForm
                      initialData={
                        initialData || {}
                      }

                      onSubmit={
                        handleSubmit
                      }

                      allowedFields={
                        allowedFields
                      }

                      customFields={{

                        /* =================================================
                           USERNAME
                        ================================================= */

                        username: {

                          label:
                            "Username",

                          type:
                            "text",

                          placeholder:
                            "Enter username",

                          /*
                            Prevent invalid characters
                            while typing.
                          */
                          onInput: (e) => {

                            e.target.value =
                              e.target.value.replace(
                                /[^A-Za-z\s]/g,
                                ""
                              );

                          },

                        },


                        /* =================================================
                           EMAIL
                        ================================================= */

                        email: {

                          label:
                            "Email",

                          type:
                            "email",

                          placeholder:
                            "Enter email",

                        },


                        /* =================================================
                           PHONE NUMBER
                        ================================================= */

                        phone_number: {

                          label:
                            "Phone Number",

                          type:
                            "text",

                          placeholder:
                            "Enter phone number",

                          maxLength:
                            10,

                          /*
                            Allow numbers only.
                          */
                          onInput: (e) => {

                            e.target.value =
                              e.target.value
                                .replace(
                                  /\D/g,
                                  ""
                                )
                                .slice(
                                  0,
                                  10
                                );

                          },

                        },


                        /* =================================================
                           USER ROLE
                        ================================================= */

                        user_role: {

                          label:
                            "User Role",

                          type:
                            "text",

                          placeholder:
                            "Enter User Role",

                          readOnly:
                            true,

                          disabled:
                            true,

                        },

                      }}
                    />


                    {/* =================================================
                        FETCH ERROR
                    ================================================= */}

                    {errors.fetch && (

                      <div className="text-danger mt-2">

                        Error: {
                          errors.fetch
                        }

                      </div>

                    )}


                    {/* =================================================
                        UPDATE ERROR
                    ================================================= */}

                    {errors.update && (

                      <div className="text-danger mt-2">

                        Error: {
                          errors.update
                        }

                      </div>

                    )}

                  </>

                )}

              </div>

            </div>

          </div>

        </div>

      </div>

    </DashboardWrapper>

  );

}